import * as THREE from 'three';

// Cache geometries and materials for better performance
const geometryCache = new Map();
const materialCache = new Map();

function getBoxGeometry(width, height, depth) {
  const key = `${width}-${height}-${depth}`;
  if (!geometryCache.has(key)) {
    geometryCache.set(key, new THREE.BoxGeometry(width, height, depth));
  }
  return geometryCache.get(key);
}

// function getMaterial(color) {
//   if (!materialCache.has(color)) {
//     materialCache.set(color, new THREE.MeshBasicMaterial({
//       color: color,
//       side: THREE.DoubleSide,
//       transparent: true,
//       opacity: 0.9
//     }));
//   }
//   return materialCache.get(color);
// }



// Option A: Disable material caching for cargos (simplest)
function getMaterial(color, isForCargo = true) {
  // For cargos, always create new materials to allow individual highlighting
  if (isForCargo) {
    return new THREE.MeshBasicMaterial({
      color: color,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.9
    });
  }
  
  // For other objects, use caching
  if (!materialCache.has(color)) {
    materialCache.set(color, new THREE.MeshBasicMaterial({
      color: color,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.9
    }));
  }
  return materialCache.get(color);
}



function getEdgeMaterial() {
  const edgeColor = 'black';
  if (!materialCache.has(edgeColor)) {
    materialCache.set(edgeColor, new THREE.LineBasicMaterial({ 
      color: edgeColor,
      linewidth: 1
    }));
  }
  return materialCache.get(edgeColor);
}

export function createCargos(cargosProps, cargo_group, options = {}) {
  const {
    showEdges = true,
    enableShadows = false,
    defaultOpacity = 0.9,
    edgeColor = 'black'
  } = options;

  if (!cargosProps || !Array.isArray(cargosProps)) {
    console.warn('createCargos: Invalid cargosProps provided');
    return [];
  }

  if (!cargo_group || !(cargo_group instanceof THREE.Group)) {
    console.error('createCargos: Valid THREE.Group required for cargo_group');
    return [];
  }

  const createdMeshes = [];

  for (let i = 0; i < cargosProps.length; i++) {
    const cargo = cargosProps[i];
    
    try {
      // Validate and parse dimensions
      const x = parseFloat(cargo.x);
      const y = parseFloat(cargo.y);
      const z = parseFloat(cargo.z);
      const width = Math.max(parseFloat(cargo.widthX));
      const height = Math.max(parseFloat(cargo.heightY));
      const depth = Math.max(parseFloat(cargo.depthZ));

      // Use cached geometry or create new one
      const geometry = getBoxGeometry(width, height, depth);
      
      // Use cached material or create new one
      const color = getColorById(cargo.layer) || getRandomColor();
      const material = getMaterial(color);

      // const material = getMaterial(color, true); // true = for cargo (no caching)


      // Create cargo mesh
      const cargoMesh = new THREE.Mesh(geometry, material);
      
      // Configure mesh properties
      cargoMesh.position.set(
        x + width / 2,
        y + height / 2,
        z + depth / 2
      );

      cargoMesh.uuid = cargo.uuid || cargoMesh.uuid;
      
      // Add user data for identification and interaction
      cargoMesh.userData = {
        type: 'cargo',
        isFloor: false,
        originalColor: color,
        cargoId: cargo.uuid || cargo.id || `cargo-${i}-${Date.now()}`,
        dimensions: { width, height, depth },
        position: { x, y, z },
        index: i,
        ...cargo.userData // Preserve any existing userData
      };

      

      // Add edges if enabled
      if (showEdges) {
        const edges = new THREE.EdgesGeometry(geometry);
        const edgeLine = new THREE.LineSegments(edges, getEdgeMaterial());
        cargoMesh.add(edgeLine);
        cargoMesh.userData.hasEdges = true;
      }

      // Enable shadows if configured
      if (enableShadows) {
        cargoMesh.castShadow = true;
        cargoMesh.receiveShadow = true;
      }

      cargo_group.add(cargoMesh);
      createdMeshes.push(cargoMesh);

    } catch (error) {
      console.error(`Failed to create cargo ${i}:`, error, cargo);
    }
  }

  console.log(`Created ${createdMeshes.length} cargos`);
  return createdMeshes;
}

export const colors = [
  {colorID: 1, colorNumber: '#1f77b4'},
  {colorID: 2, colorNumber: '#ff7f0e'},
  {colorID: 3, colorNumber: '#2ca02c'},
  {colorID: 4, colorNumber: '#d62728'},
  {colorID: 5, colorNumber: '#9467bd'},
  {colorID: 6, colorNumber: 'purple'},
  {colorID: 7, colorNumber: '#bcbd22'},
  {colorID: 8, colorNumber: '#17becf'},
  {colorID: 9, colorNumber: '#e377c2'},
  {colorID: 10, colorNumber: '#8c564b'},
  {colorID: 11, colorNumber: '#072e61ff'},
  {colorID: 12, colorNumber: '#ffbb78'},
  {colorID: 13, colorNumber: '#98df8a'},
  {colorID: 14, colorNumber: '#ff9896'},
  {colorID: 15, colorNumber: '#c5b0d5'},
  {colorID: 16, colorNumber: '#c49c94'},
  {colorID: 17, colorNumber: '#f7b6d2'},
  {colorID: 18, colorNumber: '#c7c7c7'},
  {colorID: 19, colorNumber: '#dbdb8d'},
  {colorID: 20, colorNumber: '#9edae5'},
];

// Create a map for O(1) lookup
export const colorMap = colors.reduce((map, color) => {
  map[color.colorID] = color.colorNumber;
  return map;
}, {});

// Get color by ID
export const getColorById = (colorID) => {
  return colorMap[colorID] || '#cccccc'; // default fallback
};

export function getRandomColor() {
  return colors[Math.floor(Math.random() * colors.length)];
}


// // Color management utilities
// let colorIndex = 0;

// export function getSequentialColor() {
//   const color = colors[colorIndex % colors.length];
//   colorIndex++;
//   return color;
// }

// export function resetColorIndex() {
//   colorIndex = 0;
// }

export function hexToThreeColor(hexColor) {
  return new THREE.Color(hexColor);
}

// Utility functions for cargo management
export function findCargoByUuid(cargoGroup, uuid) {
  return cargoGroup.children.find(child => 
    child.userData.type === 'cargo' && child.userData.cargoId === uuid
  );
}

export function highlightCargo(cargoMesh, highlightColor = '#ffff00') {
  if (!cargoMesh || !cargoMesh.material) return;
  
  cargoMesh.userData.originalColor = cargoMesh.material.color.getStyle();
  cargoMesh.material.color.set(highlightColor);
  cargoMesh.material.opacity = 1.0;
}

export function unhighlightCargo(cargoMesh) {
  if (!cargoMesh || !cargoMesh.material || !cargoMesh.userData.originalColor) return;
  
  cargoMesh.material.color.set(cargoMesh.userData.originalColor);
  cargoMesh.material.opacity = 0.9;
}

export function removeCargos(cargoGroup, cargoMesh) {
  if (!cargoGroup || !cargoMesh) return false;
  
  // Dispose of geometry and materials
  if (cargoMesh.geometry) cargoMesh.geometry.dispose();
  if (cargoMesh.material) {
    if (Array.isArray(cargoMesh.material)) {
      cargoMesh.material.forEach(mat => mat.dispose());
    } else {
      cargoMesh.material.dispose();
    }
  }
  
  // Remove edges
  cargoMesh.children.forEach(child => {
    if (child.geometry) child.geometry.dispose();
    if (child.material) child.material.dispose();
  });
  
  cargoGroup.remove(cargoMesh);
  return true;
}

export function updateCargoPosition(cargoMesh, newPosition) {
  if (!cargoMesh || !newPosition) return;
  
  const dimensions = cargoMesh.userData.dimensions;
  cargoMesh.position.set(
    newPosition.x + dimensions.width / 2,
    newPosition.y + dimensions.height / 2,
    newPosition.z + dimensions.depth / 2
  );
  
  cargoMesh.userData.position = { ...newPosition };
}

export function createCargosWithPhysics(cargosProps, cargo_group, options = {}) {
  const meshes = createCargos(cargosProps, cargo_group, options);
  
  // Add physics data to userData
  meshes.forEach(mesh => {
    mesh.userData.physics = {
      mass: calculateMass(mesh.userData.dimensions),
      friction: 0.5,
      restitution: 0.2,
      isStatic: false
    };
  });
  
  return meshes;
}

function calculateMass(dimensions) {
  // Simple mass calculation based on volume
  const volume = dimensions.width * dimensions.height * dimensions.depth;
  return volume * 0.1; // Assume density of 0.1 units per volume
}

export function disposeCargoResources() {
  // Clear geometry cache
  geometryCache.forEach(geometry => {
    geometry.dispose();
  });
  geometryCache.clear();

  // Clear material cache
  materialCache.forEach(material => {
    material.dispose();
  });
  materialCache.clear();
}

export function clearAllCargos(cargoGroup) {
  if (!cargoGroup) return;
  
  // Remove all cargo meshes
  const cargosToRemove = cargoGroup.children.filter(child => 
    child.userData.type === 'cargo'
  );
  
  cargosToRemove.forEach(cargo => {
    removeCargos(cargoGroup, cargo);
  });
  
  console.log(`Cleared ${cargosToRemove.length} cargos`);
}
